/**
 * The Monday–Friday week behind the attendance strip on the pickup roster.
 *
 * The strip used to walk backwards over the last five days the program *met*, which
 * answered "has this child been showing up" but never told you which day a square was:
 * on a Wednesday the leftmost square was last Thursday, and after a holiday it was some
 * other day again. An admin working dismissal thinks in the current week — "was she here
 * Monday?" — so the window is now the fixed calendar week instead of a rolling one.
 *
 * Five columns, always, Monday through Friday. Days the program did not meet (a weekday
 * off its schedule, a BHCS closure, or anything before the program started) come back
 * flagged rather than dropped, so every strip stays the same width and the columns line
 * up down a card — and a closure renders as "no session", never as an absence.
 */

import { blackoutDateSet } from '../config/blackoutDates';
import { shiftDateString, weekdayOfDateString } from './schedule';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../config/brands';

/** Squares in the strip: Monday through Friday. */
export const WEEK_DAY_COUNT = 5;

export interface WeekWindowOptions {
  /** Business-TZ day whose week is shown. YYYY-MM-DD. */
  today: string;
  /**
   * Weekdays the program meets, as `Date.getDay()` numbers (0 = Sunday). Defaults to
   * Monday–Friday, which is what every current after-school program runs.
   */
  weekdays?: ReadonlySet<number>;
  /**
   * Earliest day that can be a session — the program's start date. Before the school year
   * begins there is genuinely no history, and the week must come back with nothing marked
   * as a session rather than implying five missed days.
   */
  notBefore?: string | null;
  /**
   * Which brand's closure calendar to fall back to when `blackouts` is not supplied.
   * Defaults to the default brand — every consumer of this helper is an after-school
   * surface today, which is BHCS by definition.
   */
  brandKey?: BrandKey;
  /** Injectable for tests; defaults to the real closure calendar for `brandKey`. */
  blackouts?: ReadonlySet<string>;
}

export interface WeekDay {
  /** YYYY-MM-DD, business TZ. */
  date: string;
  /** True when after-school actually meets on that day. */
  isSession: boolean;
}

const DEFAULT_WEEKDAYS: ReadonlySet<number> = new Set([1, 2, 3, 4, 5]);

/**
 * The Monday of the week containing `ymd`.
 *
 * Saturday and Sunday resolve backwards, to the week that has just finished: nobody is
 * dismissed at the weekend, and an admin opening the board on Saturday is asking about
 * the days that just happened, not about a week that has not started.
 */
export function mondayOfWeek(ymd: string): string {
  const weekday = weekdayOfDateString(ymd);
  // `getDay()` numbers Sunday 0, so Sunday is six days *after* its Monday, not one before.
  return shiftDateString(ymd, weekday === 0 ? -6 : 1 - weekday);
}

/**
 * Monday–Friday of the week containing `today`, oldest first — always five entries.
 *
 * `isSession` is what separates a day the program met from one it did not; callers render
 * the difference, and must not read a non-session day as a day the child was missing.
 */
export function currentWeekDays(options: WeekWindowOptions): WeekDay[] {
  const weekdays = options.weekdays ?? DEFAULT_WEEKDAYS;
  // Callers that know the program pass `blackouts` themselves; the fallback names the brand
  // explicitly rather than relying on the accessor's default, so the choice is visible here.
  const blackouts = options.blackouts ?? blackoutDateSet(options.brandKey ?? DEFAULT_BRAND_KEY);
  const notBefore = options.notBefore ?? null;
  const monday = mondayOfWeek(options.today);

  return Array.from({ length: WEEK_DAY_COUNT }, (_, offset) => {
    const date = shiftDateString(monday, offset);
    return {
      date,
      isSession:
        weekdays.has(weekdayOfDateString(date)) &&
        !blackouts.has(date) &&
        (notBefore === null || date >= notBefore),
    };
  });
}
